import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Fuel, TrendingUp } from 'lucide-react';
import { equipmentAPI } from '../../services/api';
import { format } from 'date-fns';
import AddFuelLog from './AddFuelLog';

export default function FuelLogList({ equipmentId, fuelType, onRefresh }) {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    Promise.all([
      equipmentAPI.getFuelLogs(equipmentId, params),
      equipmentAPI.getFuelSummary(equipmentId),
    ]).then(([logsRes, summRes]) => {
      setList(logsRes.data || []);
      setSummary(summRes.data || null);
    }).catch(console.error).finally(() => setLoading(false));
  }, [equipmentId, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaved = () => {
    setEditing(null);
    fetchData();
    onRefresh?.();
  };

  if (loading && list.length === 0) return <div className="py-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-ga-text-subtle">Total Entries</p>
            <p className="text-xl font-bold text-ga-text-primary">{summary.total_entries}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-ga-text-subtle">Total Litres</p>
            <p className="text-xl font-bold text-amber-600">{Number(summary.total_litres).toFixed(1)} L</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-ga-text-subtle">Total Cost</p>
            <p className="text-xl font-bold text-ga-primary">₹{Number(summary.total_cost).toLocaleString()}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-ga-text-subtle">Avg Cost/Litre</p>
            <p className="text-xl font-bold text-ga-text-primary">₹{Number(summary.avg_cost_per_litre).toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Date filters */}
      <div className="flex gap-2">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" placeholder="From" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" placeholder="To" />
      </div>

      {list.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <Fuel className="h-12 w-12 mx-auto text-gray-300 mb-2" />
          No fuel logs recorded yet
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Fuel</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Litres</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Rate</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Total</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Vendor</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-sm">{row.log_date ? format(new Date(row.log_date), 'dd MMM yyyy') : '-'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      row.fuel_type === 'Diesel' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {row.fuel_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right font-medium">{Number(row.quantity_litres).toFixed(2)}</td>
                  <td className="py-3 px-4 text-right text-sm text-ga-text-muted">
                    {row.cost_per_litre ? `₹${Number(row.cost_per_litre).toFixed(2)}` : '-'}
                  </td>
                  <td className="py-3 px-4 text-right font-medium">
                    {row.total_cost ? `₹${Number(row.total_cost).toLocaleString()}` : '-'}
                  </td>
                  <td className="py-3 px-4 text-sm text-ga-text-muted">{row.vendor || row.filled_by || '-'}</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => setEditing(row)}
                      className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Edit fuel log"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AddFuelLog
          equipmentId={equipmentId}
          fuelType={fuelType}
          existingLog={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
